import { MentionType } from '@buildwise/pages/article/components/CommentsBox';
import {
  CommentReplyType,
  CommentType,
  updateArticleCommentsCache,
  updateArticleDetailsCache,
  useArticleCommentsQuery,
} from '@buildwise/pages/article/hooks/useArticleDetailsQuery';
import {
  ArticleSourceType,
  ArticleType,
  AttachmentType,
} from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import KnowledgeAPI from 'api/KnowledgeAPI';
import { AxiosResponse } from 'axios';
import useUserData from 'hooks/useUserData';
import { isEmpty, orderBy, uniqBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { catchErrorMessage, getExactFileName } from 'utility/helpers';

function useFacilitatorNotes({ article }: { article: ArticleType | undefined }) {
  const [isSubmittingFacilitatorNotes, setSubmittingFacilitatorNotes] = useState(false);
  const [facilitatorNotes, setFacilitatorNotes] = useState('');
  const [facilitatorMentions, setFacilitatorMentions] = useState<MentionType[]>([]);
  const [facilitatorAttachments, setFacilitatorAttachments] = useState<AttachmentType[]>([]);
  const { companyId, user } = useUserData();
  const { comments } = useArticleCommentsQuery({
    articleId: article?.id || '',
    shouldFetch: article?.articleType === ArticleSourceType.SUGGESTION,
  });
  const lastFacilitatorNote: CommentType | undefined = useMemo(() => {
    return orderBy(
      comments.filter(
        (comment) => comment.objectType === 'Notes' && comment.objectSubType === 'FacilitatorNotes',
      ),
      (value) => new Date(value.commentedAt),
      'desc',
    )?.[0];
  }, [comments]);

  useEffect(() => {
    if (lastFacilitatorNote) {
      setFacilitatorNotes(lastFacilitatorNote.commentText),
        setFacilitatorAttachments(lastFacilitatorNote?.attachments || []);
    }
  }, [lastFacilitatorNote]);

  const onAddFacilitatorNotes = async () => {
    if (article) {
      try {
        const _mentions = facilitatorMentions.map((m) => ({
          displayName: m.display,
          userName: m.id,
          pmuserId: m?.pmuserId,
          title: m.title,
          id: m.userId,
        }));
        setSubmittingFacilitatorNotes(true);
        if (facilitatorNotes.trim() || facilitatorAttachments.length) {
          const { data: commentResData } = await KnowledgeAPI.addComment(
            companyId,
            article?.id || '',
            {
              commentAuthor: user?.username || '',
              commentText: facilitatorNotes.trim(),
              mentions: _mentions,
              objectType: 'Notes',
              objectSubType: 'FacilitatorNotes',
            },
          );

          if (facilitatorAttachments.length > 0 && commentResData?.comment?.id) {
            const formData = new FormData();

            facilitatorAttachments.forEach((attachment) => {
              formData.append('file', attachment.file);
              if (attachment.thumbnail) {
                formData.append('thumbnail', attachment.thumbnail);
              }
            });

            const { data: commentMediaResData } = await KnowledgeAPI.addCommentMedia(
              companyId,
              commentResData?.comment?.id,
              formData,
            );
            updateArticleCommentsCache(companyId, article.id, commentMediaResData, 'add');
          } else {
            updateArticleCommentsCache(companyId, article.id, commentResData?.comment, 'add');
          }

          updateArticleDetailsCache(companyId, {
            ...article,
            totalComments: article.totalComments + 1,
          });
        }
      } catch (e) {
        toast.error(catchErrorMessage(e));
      } finally {
        setSubmittingFacilitatorNotes(false);
      }
    }
  };

  const onEditFacilitatorNotes = async () => {
    try {
      const mentions = facilitatorMentions.map((m) => ({
        displayName: m.display,
        userName: m.id,
        pmuserId: m?.pmuserId,
        title: m.title,
        id: m.userId,
      }));

      const payload = {
        id: lastFacilitatorNote.id,
        companyId: lastFacilitatorNote.companyId,
        articleId: lastFacilitatorNote.articleId,
        comment: {
          commentText: facilitatorNotes.trim(),
          commentAuthor: lastFacilitatorNote.commentAuthor,
          mentions,
          objectType: 'Notes',
          objectSubType: 'FacilitatorNotes',
        },
      };
      setSubmittingFacilitatorNotes(true);

      const newAttachments = facilitatorAttachments.filter(
        (attachment) => !attachment?.fileUploaded,
      );
      const updatedAttachments = facilitatorAttachments.filter(
        (attachment) => attachment?.fileUploaded && attachment.isUpdated,
      );
      const removedAttachments = facilitatorAttachments
        .filter((file) => file.fileUploaded && file.isRemoved)
        .map((file) => file.fileName);

      if (
        payload.comment.commentText !== lastFacilitatorNote?.commentText ||
        newAttachments.length ||
        updatedAttachments.length ||
        removedAttachments.length
      ) {
        const updateCommentPromises: Promise<AxiosResponse<any, Error> | any>[] = [];

        // check if comment text can be updated even when it is empty with attachments
        const shouldUpdateCommentText = payload.comment.commentText
          ? payload.comment.commentText !== lastFacilitatorNote?.commentText
          : lastFacilitatorNote?.attachments?.length &&
            lastFacilitatorNote?.attachments?.length !== removedAttachments.length;
        if (shouldUpdateCommentText) {
          updateCommentPromises.push(KnowledgeAPI.updateComment(payload));
        }

        if (newAttachments.length > 0) {
          const formData = new FormData();

          newAttachments.forEach((attachment) => {
            formData.append('file', attachment.file);
            if (attachment.thumbnail) {
              formData.append('thumbnail', attachment.thumbnail);
            }
          });

          updateCommentPromises.push(
            KnowledgeAPI.addCommentMedia(companyId, lastFacilitatorNote?.id, formData),
          );
        }

        if (updatedAttachments.length > 0) {
          const formData = new FormData();

          updatedAttachments.forEach((attachment) => {
            formData.append('file', attachment.file);
            if (attachment.thumbnail) {
              formData.append('thumbnail', attachment.thumbnail);
            }
          });

          updateCommentPromises.push(
            KnowledgeAPI.updateCommentMedia(companyId, lastFacilitatorNote?.id, formData),
          );
        }

        if (removedAttachments.length) {
          const removeAttachmentsPromises = removedAttachments.map((fileName) =>
            KnowledgeAPI.removeCommentMedia(companyId, lastFacilitatorNote.id, fileName),
          );
          updateCommentPromises.push(Promise.all(removeAttachmentsPromises));
        }

        const [res1, res2, res3] = await Promise.all(updateCommentPromises);

        let updatedComment: CommentType | CommentReplyType | undefined = shouldUpdateCommentText
          ? res1?.data?.comment || {}
          : res1?.data || res2?.data || lastFacilitatorNote;
        let attachments: AttachmentType[] = [];

        // collect new attachments
        if (shouldUpdateCommentText && newAttachments.length) {
          attachments = [...(res2?.data?.attachments || []), ...attachments];
        }
        if (!shouldUpdateCommentText && newAttachments.length) {
          attachments = [...(res1?.data?.attachments || []), ...attachments];
        }

        // collect updated attachments
        if (shouldUpdateCommentText && newAttachments.length && updatedAttachments.length) {
          attachments = [...(res3?.data?.attachments || []), ...attachments];
        }
        if (
          (shouldUpdateCommentText && newAttachments.length === 0 && updatedAttachments.length) ||
          (!shouldUpdateCommentText && newAttachments.length && updatedAttachments.length)
        ) {
          attachments = [...(res2?.data?.attachments || []), ...attachments];
        }
        if (!shouldUpdateCommentText && newAttachments.length === 0 && updatedAttachments.length) {
          attachments = [...(res1?.data?.attachments || []), ...attachments];
        }

        if (!isEmpty(updatedComment)) {
          // order attachments
          let finalAttachments = uniqBy(attachments, 'fileName');
          finalAttachments = facilitatorAttachments
            .filter((attachment) => !(attachment.fileUploaded && attachment.isRemoved))
            .map((attachment) => {
              return (
                attachments.find(
                  (file) =>
                    getExactFileName(file).toLowerCase() ===
                    getExactFileName(attachment).toLowerCase(),
                ) || attachment
              );
            });
          updatedComment = {
            ...updatedComment,
            attachments: finalAttachments,
          };

          updateArticleCommentsCache(companyId, article?.id || '', updatedComment, 'update');
        }
      }
    } catch (error) {
      toast.error(catchErrorMessage(error));
    } finally {
      setSubmittingFacilitatorNotes(false);
    }
  };

  const handleAddFacilitatorNotes = async () => {
    if (article && user) {
      if (lastFacilitatorNote) {
        onEditFacilitatorNotes();
      } else {
        onAddFacilitatorNotes();
      }
    }
  };

  return {
    facilitatorNotesState: {
      facilitatorNotes,
      facilitatorMentions,
      facilitatorAttachments,
      isSubmittingFacilitatorNotes,
      setFacilitatorNotes,
      setFacilitatorMentions,
      setFacilitatorAttachments,
      setSubmittingFacilitatorNotes,
    },
    handleAddFacilitatorNotes,
  };
}

export default useFacilitatorNotes;
